import axios, {Method} from 'axios'
import _ from 'lodash'
import {EntityAttributes, ET_BOOKING, ET_PERSURL} from 'module/BSEntry/EntryTypeParams'
import {AppName, getAppName} from 'util/AppName';
import {SisNameAppSpecificUrl, SisNameUrl} from 'util/SisNameUrl';
import {BSApiAction} from 'module/BSEntry/util/BSApiAction';
import {alertError, alertServerError} from 'module/ConfirmDialog/component/ConfirmDialogContainer';
import {CFMember, CFModel} from 'module/CustomField/type/CFType';
import {log} from 'common/log';
import {SisName} from 'util/SisName';
import {Booking, Persurl} from 'generated/JavaTOmodifications';
import {BSModelIDPart, BSModelType} from 'module/BSCollection/type/BSCollectionStateType';
import {AttachmentFetchMode, CustomFieldTO} from 'generated/JavaTO'
import {BSAttr} from 'module/BSEntry/BSAttr'
import {EMPTY} from 'rxjs'
import {parseServerError} from 'module/Error/util/parseServerError'

export type BSAPIModelType = Partial<Booking | Persurl> | CFModel
export type BSRequestOptions = { filterCFDstFieldNames?: string[], doNotAlertError?: boolean }

const cfAPIKeys = ['persist', 'dstField', 'pos', 'preview']; // and lazy
function filterCFDataAttr(cfMember: CFMember | undefined) {
	return _.omit(cfMember, cfAPIKeys);
}

function getCFMember(cf: CFMember[], dstField: string, pos: number): CFMember | undefined {
	return _.find(cf, {dstField, pos});
}

const filterAndMarkChangedCF = (cf: CFMember[], persistedCF: CFMember[] = [], filterCFDstFieldNames?: string[]) => {
	// remove null values from cf
	cf = (cf || []).filter(m => Object.values(filterCFDataAttr(m)).filter(x => x || x === 0).length);
	persistedCF = persistedCF || [];
	// add remove requests for already persisted but now missing members
	const removeMissing = persistedCF.filter(m => !getCFMember(cf, m.dstField, m.pos)).map(({dstField, pos}) => ({persist: true, dstField, pos}));
	// add create requests for new and change requests for members that differ
	const changedCF = cf.reduce((result, cfMember) => {
			if (!cfMember) return result;
			else if (!cfMember.dstField) {
				throw new Error("dstField missing");
			} else if (typeof cfMember.pos !== 'number') {
				throw new Error("pos missing");
			} else if (_.isEqual(
				filterCFDataAttr(cfMember),
				filterCFDataAttr(getCFMember(persistedCF, cfMember.dstField, cfMember.pos)),
			)) {
				return result;
			} else {
				return result.concat({...cfMember, persist: true});
			}
		},
		removeMissing);
	return changedCF.filter(a => _.includes(filterCFDstFieldNames, a.dstField));
};

function getRestAPIUrl(sisName: SisName) {
	let allUrls = SisNameAppSpecificUrl[getAppName()];
	if (!allUrls) allUrls = SisNameUrl;
	const url = allUrls[sisName];
	if (!url) throw new Error("Not yet implemented: " + sisName);
	return url;
}

function getEntityAttributes(sisName: SisName) {
	if (getAppName() === AppName.planTemplate) {
		return EntityAttributes[SisName.PlanTempl]
	}
	return EntityAttributes[sisName as keyof typeof EntityAttributes] || []
}


export const getBSEntryRequestParameters = (model: BSAPIModelType | undefined | null, persistedModel: BSModelIDPart | BSAPIModelType | undefined | null, restAction: BSApiAction, options?: BSRequestOptions) => {
	const {filterCFDstFieldNames} = options || {};
	model = _.cloneDeep(model);
	if (!model) model = {};
	if (model.cf) model.cf = filterAndMarkChangedCF(model.cf, (persistedModel as BSAPIModelType)?.cf ?? [], filterCFDstFieldNames)
	const sisName = model && model.sisName || persistedModel && persistedModel.sisName
	if (!sisName) throw new Error("SisName not defined!")
	let method: Method;
	let urlPart1 = getRestAPIUrl(sisName);
	let urlPart2;
	let urlPart3;
	let params = {};
	let data;
	switch (sisName) {
		case SisName.Persurl:
			if (persistedModel) {
				const {id} = persistedModel as Persurl;
				urlPart2 = encodeURIComponent(id);
			}
			break;
		case SisName.Booking:
			if (persistedModel) {
				const {bookingNr} = persistedModel as Booking;
				urlPart2 = encodeURIComponent(bookingNr);
			}
			break;
	}

	switch (restAction) {
		case BSApiAction.GET:
			method = 'GET'
			break;
		case BSApiAction.GEN: {
			method = 'PUT'
			urlPart2 = null
			urlPart3 = 'gen'
			const ea = getEntityAttributes(sisName)
			data = ea ? _.pick(model, ea) : model
		}
			break;
		case BSApiAction.SAVE: {
			if (persistedModel) method = 'PUT'
			else method = 'POST'
			const ea = getEntityAttributes(sisName)
			data = ea ? _.pick(model, ea) : model
		}
			break;
		case BSApiAction.ABL:
			urlPart3 = 'abl'
		//fallthrough
		case BSApiAction.DELETE:
			method = 'DELETE'
			params = {...params, text: model.text}
			break;
	}

	const url = _.compact([urlPart1, urlPart2, urlPart3]).join('/');

	return {
		url,
		params,
		method,
		data,
	}
};

export function getAllCFDstFieldNames(customFieldDefinitions: CustomFieldTO[] = []) {
	return customFieldDefinitions
		.filter(cfDef => cfDef.dstField.startsWith("cf."))
		.map(cfDef => cfDef.dstField.substr(3));
}


/**
 * @param options.filterCFDstFieldNames required when writing to cf namespace (use getAllCFDstFieldNames)
 */
export function doRestApiActionBSModel(
	model: BSAPIModelType | undefined,
	persistedModel: BSModelIDPart | BSAPIModelType | undefined | null,
	bsAction: BSApiAction,
	options?: BSRequestOptions,
) {
	const params = getBSEntryRequestParameters(model, persistedModel, bsAction, options);
	const req = axios.request<BSModelType | null>(params)
	if (!options?.doNotAlertError) {
		req.catch(error => void alertServerError(error));
	}
	return req;
}

const _fetchLazyCFAttributesRequests = {};
const _fetchLazyCFAttrLinkRequests = {};

export const getCurrentRequestFromCache = (cache: any, sisName: SisName, id: number | string | null, dstField: string, pos: number | undefined, fetchMode: AttachmentFetchMode) => {
	if (id && pos) return _.get(cache, [sisName, id, dstField, pos, fetchMode]);
};

export function fetchCFAttributesFromModelAPI(model: CFModel) {
	if (!model) return []
	const id = BSAttr.getId(model);
	const {sisName} = model;
	if (!id || !sisName || sisName !== SisName.Persurl && sisName !== SisName.Booking || !model.cf) return EMPTY
	const lazyFields = model.cf?.filter(cf => !cf.preview && !cf.data && cf.lazy).map(cf => ({dstField: cf.dstField, pos: cf.pos}))
	return lazyFields?.map(({dstField, pos}) => fetchCFAttributeAPI(sisName, id, dstField, pos, AttachmentFetchMode.PREVIEW)) || []
}

export function fetchCFAttributeAPI(sisName: SisName, id: number | string, dstField: string, pos: number | undefined, fetchMode: AttachmentFetchMode): Promise<CFMember> {
	return _fetch('../json/attachment/get', _fetchLazyCFAttributesRequests, sisName, id, dstField, pos, fetchMode)
}

export function fetchCFLinkAPI(sisName: SisName, id: number | string | null, dstField: string, pos: number | undefined, fetchMode: AttachmentFetchMode, bookingType?: string, hourCategory?: string, persNr?: number): Promise<CFMember> {
	return _fetch('../json/attachment/link', _fetchLazyCFAttrLinkRequests, sisName, id, dstField, pos, fetchMode, bookingType, hourCategory, persNr)
}

function _fetch(url: string, cache: any, sisName: SisName, id: number | string | null, dstField: string, pos: number | undefined, fetchMode: AttachmentFetchMode, bookingType?: string, hourCategory?: string, persNr?: number): Promise<CFMember> {
	const currentReq = getCurrentRequestFromCache(cache, sisName, id, dstField, pos, fetchMode);
	if (currentReq) return currentReq;
	switch (sisName) {
		case ET_PERSURL:
		case ET_BOOKING:
			const req = axios.post<CFMember>(url, {
				dstField,
				pos,
				id,
				sisName,
				fetchMode: fetchMode || AttachmentFetchMode.EAGER,
				bookingType,
				hourCategory,
				persNr,
			}).then(({data}) => data);
			if (id && pos) _.set(cache, [sisName, id, dstField, pos, fetchMode], req);
			req.catch(error => void alertError(
				`Fehler beim Laden des erweiterten Attributs "${dstField}, Position: ${pos} \n${parseServerError(error)}"`,
			)).then(() => {
				if (id && pos) _.set(cache, [sisName, id, dstField, pos, fetchMode], undefined);
			}); // remove from req cache
			return req;
		default :
			log.error("unknown sisName attachment fetch", sisName);
			throw new Error("Unbekannter Eintrag")
	}
}

export const _test = {filterAndMarkChangedCF};